import { useMemo } from "react"
import { Card, CardBody, Col, Row } from "reactstrap"
import { formatReadableDate } from "utils/formatDate"
import { formatBDT } from "utils/formatPrice"

const escapeHtml = value =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;")

const formatRichText = value => {
  const text = String(value ?? "").trim()

  if (!text) {
    return "-"
  }

  const hasHtml = /<[^>]+>/.test(text)

  if (hasHtml) {
    return text
  }

  return escapeHtml(text).replace(/\r\n|\r|\n/g, "<br />")
}

const QuotationDetailsCard = ({
  quotation = {},
  isLoading = false,
  emptyLabel = "No quotation details available.",
}) => {
  const pdfContent = quotation?.pdf_content ?? {}
  const vehicleSpecs = pdfContent?.vehicle_specs ?? []
  const priceItems = pdfContent?.price_items ?? []
  const hasQuotationContent = Boolean(
    quotation?.id ||
    quotation?.qid ||
    Object.keys(pdfContent).length
  )

  const customerBlock = useMemo(() => {
    if (pdfContent?.customer_info) return pdfContent.customer_info
    return [
      quotation?.customer?.name,
      quotation?.customer?.mobile,
      quotation?.customer?.email,
      quotation?.customer?.full_address,
    ]
      .filter(Boolean)
      .join("\n")
  }, [pdfContent, quotation])

  const richSubject = useMemo(() => formatRichText(pdfContent?.subject), [pdfContent?.subject])
  const richCustomerBlock = useMemo(() => formatRichText(customerBlock), [customerBlock])
  const richBodyText = useMemo(() => formatRichText(pdfContent?.body_text), [pdfContent?.body_text])
  const richPaymentTerms = useMemo(
    () => formatRichText(pdfContent?.payment_terms),
    [pdfContent?.payment_terms]
  )
  const richClosingText = useMemo(
    () => formatRichText(pdfContent?.closing_text),
    [pdfContent?.closing_text]
  )

  return (
    <Card>
      <CardBody>
        {isLoading ? (
          <div>Loading...</div>
        ) : !hasQuotationContent ? (
          <div className="text-muted">{emptyLabel}</div>
        ) : (
          <Row className="g-4">
            <Col lg={7}>
              <div className="d-flex justify-content-between align-items-start gap-3 mb-3 flex-wrap">
                <div>
                  <h5 className="mb-1">{quotation?.qid ?? "Quotation"}</h5>
                  <div className="text-muted">
                    {formatReadableDate(pdfContent?.quotation_date, "MMM d, y")} to{" "}
                    {formatReadableDate(pdfContent?.valid_until, "MMM d, y")}
                  </div>
                </div>
              </div>

              <div className="mb-3">
                <div className="fw-semibold">Subject</div>
                <div dangerouslySetInnerHTML={{ __html: richSubject }} />
              </div>

              <div className="mb-3">
                <div className="fw-semibold">Customer Information</div>
                <div dangerouslySetInnerHTML={{ __html: richCustomerBlock }} />
              </div>

              <div className="mb-3">
                <div className="fw-semibold">Body Text</div>
                <div dangerouslySetInnerHTML={{ __html: richBodyText }} />
              </div>

              <div className="mb-3">
                <div className="fw-semibold">Payment Terms</div>
                <div dangerouslySetInnerHTML={{ __html: richPaymentTerms }} />
              </div>

              <div className="mb-3">
                <div className="fw-semibold">Closing Text</div>
                <div className="text-muted" dangerouslySetInnerHTML={{ __html: richClosingText }} />
              </div>
            </Col>

            <Col lg={5}>
              <h5 className="mb-3">Pricing & Vehicle</h5>

              <Card className="border shadow-none mb-3">
                <CardBody>
                  <div className="fw-semibold mb-2">Price Items</div>
                  {priceItems.map(item => (
                    <div
                      key={item?.id}
                      className="d-flex justify-content-between border-bottom py-2"
                    >
                      <span>{item?.description}</span>
                      <span>{formatBDT(item?.amount)}</span>
                    </div>
                  ))}
                  <div className="d-flex justify-content-between pt-3 fw-bold text-success">
                    <span>Total</span>
                    <span>{formatBDT(pdfContent?.total_amount)}</span>
                  </div>
                  <div className="small text-muted mt-2">
                    {pdfContent?.amount_in_words ?? "-"}
                  </div>
                </CardBody>
              </Card>

              <Card className="border shadow-none">
                <CardBody>
                  <div className="fw-semibold mb-2">Vehicle Specifications</div>
                  {vehicleSpecs.length ? (
                    vehicleSpecs.map(spec => (
                      <div
                        key={`${spec?.key}-${spec?.value}`}
                        className="d-flex justify-content-between border-bottom py-2"
                      >
                        <span className="text-muted">{spec?.key}</span>
                        <span className="text-end ms-2">{spec?.value || "-"}</span>
                      </div>
                    ))
                  ) : (
                    <div className="text-muted">No vehicle specs available.</div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}
      </CardBody>
    </Card>
  )
}

export default QuotationDetailsCard
